import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import DeepSpaceBackground from './components/DeepSpaceBackground';

const tiles = [
  {
    id: 'overview',
    label: 'Overview',
    subtitle: 'Technology and site status across all locations',
    path: '/overview',
  },
  {
    id: 'operations',
    label: 'Operations',
    subtitle: 'Staff, access, and location controls',
    path: '/admin',
  },
  {
    id: 'support',
    label: 'Support',
    subtitle: 'Tickets and TCB helpdesk',
    path: '/support',
  },
];

const HomePage = () => {
  return (
    <div className="hp-root">
      <style>{`
        .hp-root {
          min-height: 100vh;
          height: 100vh;
          width: 100%;
          position: relative;
          overflow: hidden;
          color: #ffffff;
          background-color: #000000;
          font-family: SF Pro Text, SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Inter, Arial, Helvetica, sans-serif;
          --figma-font-text: SF Pro Text, SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Inter, Arial, Helvetica, sans-serif;
          --tile-fill: rgba(255, 255, 255, 0.03);
          --tile-border: rgba(255, 255, 255, 0.1);
          --tile-border-strong: rgba(255, 255, 255, 0.18);
          --preview-fill: rgba(0, 0, 0, 0.3);
          isolation: isolate;
        }

        .hp-root *,
        .hp-root *::before,
        .hp-root *::after {
          box-sizing: border-box;
          font-family: inherit;
        }

        .hp-deep-space {
          position: fixed;
          inset: 0;
          z-index: 0;
          overflow: hidden;
          pointer-events: none;
        }

        .hp-deep-space-canvas {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
        }

        .hp-brand-header,
        .hp-brand-footer {
          position: fixed;
          left: 50%;
          transform: translateX(-50%);
          z-index: 30;
          pointer-events: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hp-brand-header {
          top: clamp(1.2rem, 3.4vh, 2.4rem);
        }

        .hp-brand-footer {
          bottom: clamp(1rem, 2.8vh, 1.8rem);
        }

        .hp-brand-logo {
          display: block;
          width: auto;
          object-fit: contain;
          filter: brightness(0) invert(1) drop-shadow(0 10px 26px rgba(0, 0, 0, 0.72));
          opacity: 0.96;
        }

        .hp-brand-logo-main {
          height: clamp(2.3rem, 5vw, 3.8rem);
        }

        .hp-brand-logo-footer {
          height: clamp(2rem, 3.3vw, 2.8rem);
          opacity: 0.78;
        }

        .hp-shell {
          position: relative;
          z-index: 10;
          width: min(1400px, calc(100% - 2.5rem));
          min-height: 100vh;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: clamp(6rem, 13vh, 8.5rem) 0 clamp(5.4rem, 11vh, 7.4rem);
        }

        .hp-main {
          width: 100%;
        }

        .hp-tile-grid {
          width: 100%;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: clamp(0.85rem, 1.4vw, 1.15rem);
        }

        .hp-tile-wrap {
          min-width: 0;
          border-radius: 28px;
          text-decoration: none;
          color: inherit;
          transition: transform 180ms ease, opacity 180ms ease;
        }

        .hp-tile-wrap:hover,
        .hp-tile-wrap:focus-visible {
          outline: none;
        }

        .hp-tile-wrap:hover {
          transform: translateY(-2px);
        }

        .hp-tile-wrap:focus-visible {
          outline: 1px solid rgba(255, 255, 255, 0.56);
          outline-offset: 5px;
        }

        .hp-tile {
          min-height: clamp(31rem, 58vh, 39.5rem);
          height: 100%;
          position: relative;
          overflow: hidden;
          border-radius: 28px;
          padding: clamp(1.28rem, 1.85vw, 1.7rem);
          border: 1px solid var(--tile-border);
          background-color: var(--tile-fill);
          backdrop-filter: blur(64px) saturate(200%);
          -webkit-backdrop-filter: blur(64px) saturate(200%);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.16),
            inset 0 -1px 0 rgba(255, 255, 255, 0.04),
            0 30px 86px rgba(0, 0, 0, 0.44);
        }

        .hp-tile-wrap:hover .hp-tile,
        .hp-tile-wrap:focus-visible .hp-tile {
          border-color: var(--tile-border-strong);
          background-color: rgba(255, 255, 255, 0.045);
        }

        .hp-tile-inner {
          position: relative;
          min-height: 100%;
          display: grid;
          grid-template-rows: auto minmax(0, 1fr);
          gap: clamp(0.9rem, 1.25vw, 1.15rem);
        }

        .hp-tile-head {
          min-height: clamp(5.2rem, 7.6vh, 6rem);
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
        }

        .hp-card-title {
          margin: 0;
          color: #ffffff;
          font-family: var(--figma-font-text);
          font-size: clamp(1.2rem, 1.28vw, 1.34rem);
          line-height: 1.06;
          letter-spacing: 0;
          font-weight: 740;
          text-shadow: none;
        }

        .hp-card-subtitle {
          max-width: 21rem;
          margin: 0.58rem 0 0;
          color: rgba(255, 255, 255, 0.62);
          font-family: var(--figma-font-text);
          font-size: 16px;
          line-height: 21px;
          letter-spacing: 0;
          font-weight: 580;
          text-shadow: none;
        }

        .hp-open {
          width: 56px;
          height: 56px;
          flex: 0 0 auto;
          display: grid;
          place-items: center;
          border-radius: 999px;
          color: rgba(255, 255, 255, 1);
          border: 1px solid rgba(255, 255, 255, 0.12);
          background-color: rgba(255, 255, 255, 0.045);
          backdrop-filter: blur(36px) saturate(180%);
          -webkit-backdrop-filter: blur(36px) saturate(180%);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.16),
            0 16px 34px rgba(0, 0, 0, 0.38);
        }

        .hp-preview-window {
          min-height: 0;
          height: min(100%, clamp(21.5rem, 43vh, 28.5rem));
          align-self: start;
          position: relative;
          overflow: hidden;
          border-radius: 18px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          background-color: var(--preview-fill);
          box-shadow:
            inset 0 24px 48px rgba(0, 0, 0, 0.62),
            inset 0 -1px 0 rgba(255, 255, 255, 0.035),
            inset 0 1px 0 rgba(255, 255, 255, 0.045);
        }

        .hp-preview-frame {
          position: absolute;
          top: 0;
          left: 0;
          width: 285%;
          height: 285%;
          border: 0;
          pointer-events: none;
          transform: scale(0.35);
          transform-origin: top left;
          background-color: #000000;
          filter: saturate(0.94) brightness(0.86);
        }

        @media (max-width: 1060px) {
          .hp-root {
            height: auto;
            min-height: 100vh;
            overflow-y: auto;
          }

          .hp-brand-header,
          .hp-brand-footer {
            position: absolute;
          }

          .hp-shell {
            width: min(42rem, calc(100% - 2rem));
            align-items: flex-start;
            padding-top: clamp(6.4rem, 14vh, 8rem);
            padding-bottom: clamp(5.8rem, 11vh, 7rem);
          }

          .hp-tile-grid {
            grid-template-columns: 1fr;
          }

          .hp-tile {
            min-height: 28rem;
          }
        }

        @media (max-width: 520px) {
          .hp-shell {
            width: calc(100% - 1.5rem);
          }

          .hp-tile {
            min-height: 25rem;
            border-radius: 24px;
            padding: 1rem;
          }

          .hp-preview-window {
            border-radius: 16px;
          }

          .hp-brand-logo-main {
            height: 2.25rem;
          }

          .hp-brand-logo-footer {
            height: 1.9rem;
          }
        }
      `}</style>

      <DeepSpaceBackground />

      <header className="hp-brand-header" aria-label="Launchpad brand">
        <img
          src="/images/MJG_Logo.png"
          alt="McJunkins Group"
          className="hp-brand-logo hp-brand-logo-main"
        />
      </header>

      <div className="hp-shell">
        <main className="hp-main">
          <div className="hp-tile-grid">
            {tiles.map((tile) => (
              <NavigationTile key={tile.id} tile={tile} />
            ))}
          </div>
        </main>
      </div>

      <footer className="hp-brand-footer" aria-label="Technology partner">
        <img
          src="/images/tcb-logo.png"
          alt="TCB Network Solutions"
          className="hp-brand-logo hp-brand-logo-footer"
        />
      </footer>
    </div>
  );
};

const NavigationTile = ({ tile }) => (
  <Link
    to={tile.path}
    className="hp-tile-wrap"
    aria-label={`Open ${tile.label}`}
  >
    <article className="hp-tile">
      <div className="hp-tile-inner">
        <div className="hp-tile-head">
          <div>
            <h2 className="hp-card-title">{tile.label}</h2>
            <p className="hp-card-subtitle">{tile.subtitle}</p>
          </div>
          <span className="hp-open" aria-hidden="true">
            <ArrowRight size={16} strokeWidth={1.8} />
          </span>
        </div>

        <PreviewWindow tile={tile} />
      </div>
    </article>
  </Link>
);

const PreviewWindow = ({ tile }) => (
  <div className="hp-preview-window" aria-hidden="true">
    <iframe
      className="hp-preview-frame"
      data-preview-fit="full-page"
      src={`${tile.path}?preview=true`}
      title={`${tile.label} live preview`}
      tabIndex="-1"
    />
  </div>
);

export default HomePage;
